import React, { useState, useCallback, useEffect } from 'react';
import { ArrowLeft, ArrowUpRight } from 'lucide-react';
import { portfolioImages, youtubeVideos, CATEGORIES, categoryCovers } from '../mock';
import { ImageLightbox, VideoModal, VideoThumb } from './PortfolioMedia';

const getImagesByCategory = (cat) => portfolioImages.filter((img) => img.category === cat);

// --- Category Card ---

const CategoryCard = ({ cat, count, thumbnail, onClick }) => {
  return (
    <button
      onClick={onClick}
      className="group text-left bg-[#0A0A0B] border border-[#3d2a5c]/10 hover:border-[#14B8A6]/40 transition-all duration-300 cursor-pointer focus:outline-none focus:border-[#14B8A6]/60 overflow-hidden"
      data-testid={`category-card-${cat.id}`}
    >
      <div className="aspect-[4/3] overflow-hidden bg-[#0f0b18] relative">
        {thumbnail ? (
          <img
            src={thumbnail}
            alt={`${cat.label} preview`}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-[#0A0A0B]/30 to-transparent" />
      </div>
      <div className="p-5 lg:p-6">
        <h3 className="text-xl font-medium text-[#F0EDE8] mb-2 group-hover:text-[#14B8A6] transition-colors">{cat.label}</h3>
        <p className="text-[#E8E4DF]/55 text-sm mb-3">{cat.description}</p>
        <span className="text-[#E8E4DF]/30 text-xs" data-font="label">{count} {count === 1 ? 'piece' : 'pieces'}</span>
      </div>
    </button>
  );
};

// --- Gallery Image ---

const GalleryImage = ({ img, onClick }) => (
  <div
    className="group relative overflow-hidden cursor-pointer aspect-[3/4] corner-frame"
    onClick={onClick}
    data-testid={`gallery-image-${img.id}`}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
  >
    <img
      src={img.image}
      alt={`${img.title} - AI visual by Whisky Woven`}
      className="w-full h-full object-contain bg-[#0A0A0B] transition-transform duration-500 group-hover:scale-[1.03]"
      loading="lazy"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <h3 className="text-sm font-medium text-[#F0EDE8]">{img.title}</h3>
        {img.description && <p className="text-[#E8E4DF]/50 text-xs mt-0.5">{img.description}</p>}
      </div>
    </div>
  </div>
);

// --- Tab Bar ---

const TabBar = ({ activeTab, onTabChange }) => {
  const tabs = [{ id: 'hub', label: 'Categories' }, { id: 'all', label: 'All Work' }];
  return (
    <div className="flex gap-2 mb-10 justify-center" data-font="label">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          className={`px-8 py-3 text-sm uppercase tracking-[0.12em] font-medium transition-all duration-300 ${
            activeTab === tab.id
              ? 'bg-[#14B8A6] text-[#0A0A0B] hover:bg-[#0D9488] hover:shadow-lg hover:shadow-[#14B8A6]/20'
              : 'border-2 border-[#F0EDE8]/40 text-[#F0EDE8] hover:text-[#14B8A6] hover:border-[#14B8A6]/80 hover:bg-[#14B8A6]/5'
          }`}
          data-testid={`portfolio-tab-${tab.id}`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
};

// --- Category Gallery View ---

const CategoryGallery = ({ category, onBack, onImageClick, onVideoClick }) => {
  const isVideo = category.id === 'videos';
  const images = getImagesByCategory(category.id);

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-2 text-[#14B8A6] hover:text-[#0D9488] mb-8 text-sm" data-font="label" data-testid="gallery-back-btn">
        <ArrowLeft className="w-4 h-4" /> All Categories
      </button>
      <h3 className="text-2xl font-medium text-[#F0EDE8] mb-2">{category.label}</h3>
      <p className="text-[#E8E4DF]/50 mb-8">{category.description}</p>

      {isVideo ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {youtubeVideos.map((video, idx) => (
            <VideoThumb key={video.id} video={video} onClick={() => onVideoClick(idx)} />
          ))}
        </div>
      ) : images.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3">
          {images.map((img) => (
            <GalleryImage
              key={img.id}
              img={img}
              onClick={() => onImageClick(images.indexOf(img), images)}
            />
          ))}
        </div>
      ) : (
        <p className="text-[#E8E4DF]/30 text-center py-12">More work coming soon.</p>
      )}
    </div>
  );
};

// --- All Work View ---

const AllWorkView = ({ onImageClick, onVideoClick }) => (
  <div className="space-y-16">
    {CATEGORIES.map((cat) => {
      const isVideo = cat.id === 'videos';
      const images = getImagesByCategory(cat.id);
      const hasContent = isVideo ? youtubeVideos.length > 0 : images.length > 0;
      if (!hasContent) return null;

      return (
        <div key={cat.id}>
          <div className="flex items-center gap-3 mb-6">
            <span className="label-sm text-[#14B8A6]" data-font="label">{cat.label}</span>
            <div className="flex-1 h-px bg-[#3d2a5c]/15" />
          </div>
          {isVideo ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {youtubeVideos.map((video, idx) => (
                <VideoThumb key={video.id} video={video} onClick={() => onVideoClick(idx)} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3">
              {images.map((img) => (
                <GalleryImage key={img.id} img={img} onClick={() => onImageClick(images.indexOf(img), images)} />
              ))}
            </div>
          )}
        </div>
      );
    })}
  </div>
);

// --- Portfolio Hub (Main) ---

const PortfolioHub = () => {
  const [view, setView] = useState('hub');
  const [activeCategory, setActiveCategory] = useState(null);
  const [lightboxItems, setLightboxItems] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [videoIndex, setVideoIndex] = useState(null);

  const getCategoryCount = useCallback((catId) => {
    if (catId === 'videos') return youtubeVideos.length;
    return getImagesByCategory(catId).length;
  }, []);

  const getCategoryThumbnail = useCallback((catId) => {
    if (categoryCovers[catId]) return categoryCovers[catId];
    if (catId === 'videos') return youtubeVideos[0]?.thumbnail || null;
    return getImagesByCategory(catId)[0]?.image || null;
  }, []);

  const openCategory = (cat) => { setActiveCategory(cat); setView('gallery'); };
  const backToHub = () => { setActiveCategory(null); setView('hub'); };

  const openLightbox = (index, items) => { setLightboxItems(items); setLightboxIndex(index); };
  const closeLightbox = () => { setLightboxItems(null); };
  const prevImage = () => { if (lightboxItems) setLightboxIndex((i) => (i - 1 + lightboxItems.length) % lightboxItems.length); };
  const nextImage = () => { if (lightboxItems) setLightboxIndex((i) => (i + 1) % lightboxItems.length); };

  const openVideo = (index) => setVideoIndex(index);
  const closeVideo = () => setVideoIndex(null);
  const prevVideo = () => setVideoIndex((i) => (i - 1 + youtubeVideos.length) % youtubeVideos.length);
  const nextVideo = () => setVideoIndex((i) => (i + 1) % youtubeVideos.length);

  useEffect(() => {
    const handleKey = (e) => {
      if (lightboxItems) {
        if (e.key === 'ArrowLeft') prevImage();
        if (e.key === 'ArrowRight') nextImage();
        if (e.key === 'Escape') closeLightbox();
      }
      if (videoIndex !== null) {
        if (e.key === 'ArrowLeft') prevVideo();
        if (e.key === 'ArrowRight') nextVideo();
        if (e.key === 'Escape') closeVideo();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  });

  return (
    <section id="work" className="py-24 lg:py-36 bg-[#0A0A0B]" data-testid="portfolio-section">
      <div className="container mx-auto px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="label-sm text-[#14B8A6] mb-4" data-font="label">Portfolio</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-[#F0EDE8] mb-5" data-testid="portfolio-heading">
              Step into the worlds
            </h2>
            <p className="text-[#E8E4DF] max-w-lg mx-auto">
              Explore the full body of work — character portraits, creatures, world imagery, and cinematic video trailers.
            </p>
          </div>

          {view !== 'gallery' && (
            <TabBar activeTab={view} onTabChange={setView} />
          )}

          {view === 'hub' && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
                {CATEGORIES.map((cat) => (
                  <CategoryCard
                    key={cat.id}
                    cat={cat}
                    count={getCategoryCount(cat.id)}
                    thumbnail={getCategoryThumbnail(cat.id)}
                    onClick={() => openCategory(cat)}
                  />
                ))}
              </div>
              <div className="text-center mt-14" data-font="label">
                <a
                  href="https://gingerscreativeventures.com/"
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#14B8A6] hover:bg-[#0D9488] text-[#0A0A0B] font-medium px-8 py-3.5 text-sm uppercase tracking-[0.12em] transition-all duration-300 hover:shadow-lg hover:shadow-[#14B8A6]/20"
                  data-testid="view-full-portfolio-btn"
                >
                  View Full Portfolio
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            </>
          )}

          {view === 'gallery' && activeCategory && (
            <CategoryGallery
              category={activeCategory}
              onBack={backToHub}
              onImageClick={openLightbox}
              onVideoClick={openVideo}
            />
          )}

          {view === 'all' && (
            <AllWorkView onImageClick={openLightbox} onVideoClick={openVideo} />
          )}
        </div>
      </div>

      {lightboxItems && (
        <ImageLightbox items={lightboxItems} currentIndex={lightboxIndex} onClose={closeLightbox} onPrev={prevImage} onNext={nextImage} />
      )}
      {videoIndex !== null && (
        <VideoModal videos={youtubeVideos} currentIndex={videoIndex} onClose={closeVideo} onPrev={prevVideo} onNext={nextVideo} />
      )}
    </section>
  );
};

export default PortfolioHub;
